import { ColumnDef } from '@tanstack/react-table'
import { TextCell } from 'components/table/cells/TextCell'
import { ThumbnailCell } from 'components/table/cells/ThumbnailCell'
import { Thumbnail } from 'components/table/utility/Thumbnail'
import {
  AssetImage,
  AssetTableAssetViz,
  convertDateToString,
} from 'features/assetDatabase'
import { ShipData } from 'features/shipDatabase/types/shipTypes'
import { InputType } from 'types'

export const createNavyShipTableColumns = (
  siteID: string
): ColumnDef<ShipData>[] => {
  const defaultColumns: ColumnDef<ShipData>[] = [
    {
      accessorKey: 'name',
      id: 'name',
      header: 'Ship',
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
      sortUndefined: 'last',
      meta: {
        inputType: InputType.Text,
      },
      cell: ({ row }) => {
        return (
          <ThumbnailCell
            data-testid={`asset-${row.original.id}`}
            thumbnailString={row.getValue('name')}
            to='/assets/$siteID/$unitID'
            from='/ships/$siteID'
            params={{ siteID, unitID: row.original.id }}
            search={{
              view: 'details',
            }}>
            <Thumbnail>
              <AssetImage
                primaryImageID={row.original.unitPrimaryImage}
                assetModelType={row.original.assetModelType}
              />
            </Thumbnail>
          </ThumbnailCell>
        )
      },
    },
    {
      accessorKey: 'class',
      id: 'class',
      header: 'Class',
      filterFn: 'multiSelectFilter',
      sortingFn: 'alphanumeric',
      sortUndefined: 'last',
      meta: {
        inputType: InputType.MultiSelect,
      },
      cell: ({ row }) => <TextCell text={row.getValue('class')} />,
    },
    {
      accessorKey: 'fleet',
      id: 'fleet',
      header: 'Fleet',
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
      sortUndefined: 'last',
      meta: {
        inputType: InputType.MultiSelect,
      },
      cell: ({ row }) => <TextCell text={row.getValue('fleet')} />,
    },
    {
      accessorKey: 'commissionDate',
      id: 'commissionDate',
      header: 'Commission Date',
      filterFn: 'dateRangeFilter',
      sortingFn: 'datetime',
      sortUndefined: 'last',
      meta: {
        inputType: InputType.Date,
      },
      cell: ({ row }) => (
        <TextCell text={convertDateToString(row.getValue('commissionDate'))} />
      ),
    },
    {
      accessorKey: 'homeport',
      id: 'homeport',
      header: 'Home Port',
      filterFn: 'multiSelectFilter',
      sortingFn: 'alphanumeric',
      sortUndefined: 'last',
      meta: {
        inputType: InputType.MultiSelect,
      },
      cell: ({ row }) => <TextCell text={row.getValue('homeport')} />,
    },
    {
      accessorKey: 'lastInspectionDate',
      id: 'lastInspectionDate',
      header: 'Last Inspection',
      sortingFn: 'datetime',
      sortUndefined: 'last',
      filterFn: 'dateRangeFilter',
      meta: {
        inputType: InputType.Date,
      },
      cell: ({ row }) => (
        <ThumbnailCell
          thumbnailString={convertDateToString(
            row.getValue('lastInspectionDate')
          )}
          to={
            convertDateToString(row.original.lastInspectionDate)
              ? '/details/$entryID'
              : '/assets/$siteID/$unitID'
          }
          from='/ships/$siteID'
          params={{
            entryID: row.original.lastInspectionSlug,
            siteID,
            unitID: row.original.id,
          }}
          search={{ view: 'details' }}
          hoverable={Boolean(row.original.lastInspectionDate)}
          emptyText={'Not yet inspected'}>
          {
            <Thumbnail>
              <AssetTableAssetViz
                cardProps={row.original.cardProps}
                unitID={row.original.id}
                siteID={siteID}
              />
            </Thumbnail>
          }
        </ThumbnailCell>
      ),
    },
  ]

  return defaultColumns
}
